import React, { useState } from 'react';
import { RiMapPin2Line, RiPhoneLine, RiMailLine, RiTimeLine, RiUserLine, RiMessageLine, RiSendPlaneLine, RiCheckLine } from 'react-icons/ri';
import { useApiCall } from '../hooks/useApiCall';
import { useToast } from '../contexts/ToastContext';
import '../styles/Contact.css';

const Contact = () => {
  const apiCall = useApiCall();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const response = await apiCall('contact/submit', 'POST', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      }, null, null);
      
      if (response.success) {
        setIsSubmitted(true);
        showSuccess('Message sent successfully! We\'ll get back to you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        showError(response.response || 'Failed to send message. Please try again.');
        setErrors({ general: response.response || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      showError('Failed to send message. Please try again.');
      setErrors({ general: 'Failed to send message. Please try again.' });
      console.error('Error submitting contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <RiMapPin2Line />,
      title: 'Visit Our Showroom',
      details: [
        'Henck Arronstraat 45',
        'Paramaribo, Suriname'
      ]
    },
    {
      icon: <RiPhoneLine />,
      title: 'Call Us',
      details: [
        '+597 123-4567',
        '+597 765-4321'
      ]
    },
    {
      icon: <RiMailLine />,
      title: 'Email Us',
      details: [
        'info@buildingdepotsuriname.com',
        'support@buildingdepotsuriname.com'
      ]
    },
    {
      icon: <RiTimeLine />,
      title: 'Business Hours',
      details: [
        'Mon-Fri: 8:00 AM - 6:00 PM',
        'Sat: 9:00 AM - 4:00 PM',
        'Sun: Closed'
      ]
    }
  ];

  if (isSubmitted) {
    return (
      <div className="contact-page">
        <div className="contact-container">
          <div className="contact-success">
            <RiCheckLine className="success-icon" />
            <h1>Message Sent Successfully!</h1>
            <p>Thank you for contacting Building Depot Suriname. We'll get back to you within 24 hours.</p>
            <button 
              className="btn-back"
              onClick={() => setIsSubmitted(false)}
            >
              Send Another Message
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="contact-container">
        
        <div className="contact-header">
          <h1>Contact Us</h1>
          <p>Get in touch with Building Depot Suriname for lighting consultations, support, or any questions</p>
        </div>

        <div className="contact-content">
          {/* Contact Info Section */}
          <div className="contact-info">
            <h2>Get In Touch</h2>
            <div className="contact-info-grid">
              {contactInfo.map((info, index) => (
                <div key={index} className="contact-info-card">
                  <div className="contact-info-icon">
                    {info.icon}
                  </div>
                  <div className="contact-info-content">
                    <h3>{info.title}</h3>
                    {info.details.map((detail, idx) => (
                      <p key={idx}>{detail}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-form-section">
            <h2>Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="contact-form">
              {errors.general && (
                <div className="error-message general-error">
                  {errors.general}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <div className="input-wrapper">
                    <RiUserLine className="input-icon" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className={errors.name ? 'error' : ''}
                    />
                  </div>
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <div className="input-wrapper">
                    <RiMailLine className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className={errors.email ? 'error' : ''}
                    />
                  </div>
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="input-wrapper">
                    <RiPhoneLine className="input-icon" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <div className="input-wrapper">
                    <RiMessageLine className="input-icon" />
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What can we help you with?"
                      className={errors.subject ? 'error' : ''}
                    />
                  </div>
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <div className="textarea-wrapper">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your lighting needs or questions..."
                    rows="5"
                    className={errors.message ? 'error' : ''}
                  />
                </div>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>Sending Message...</>
                ) : (
                  <>
                    <RiSendPlaneLine />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="contact-map">
          <h2>Find Our Showroom</h2>
          <div className="map-container">
            <div className="map-placeholder">
              <RiMapPin2Line className="map-icon" />
              <p>Interactive map coming soon</p>
              <p>Henck Arronstraat 45, Paramaribo, Suriname</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Contact;